import { gql, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { usePosition } from '../../hooks/usePosition';
import Shuttle from '../../models/Shuttle';
// import ShuttleMarker from '../ShuttleMarker/ShuttleMarker';
import { ITrackerOverlay } from '../Tracker/TrackerOverlay';
import './Map.css';
import SVG from 'react-inlinesvg';
import markerIcon from '../ic_tracking_marker.svg';

const center = { lat: 39.65375210217095, lng: -74.19196917634842 };
const defaultZoom = 16;

interface ShuttleData {
  shuttlesByLocation: Shuttle[];
}

export const GET_SHUTTLES = gql`
  query GetShuttles($lat: Float, $lng: Float) {
    shuttlesByLocation(lat: $lat, lng: $lng) {
      id
      name
      color
      sponsorName
      sponsorWebsite
      photo
      lat
      lng
      distance
      online
      updatedAt
    }
  }
`;

interface MapProps {
  selectedShuttle?: Shuttle;
  onShuttleSelected: (shuttle?: Shuttle) => void;
  pollInterval: number;
}

const isOnline = (shuttle: Shuttle) => {
  const now = new Date();
  const updatedAt = new Date(shuttle.updatedAt.toString())
  const ms = now.getTime() - updatedAt.getTime();
  const oneHour = 1000 * 60 * 60;;
  return shuttle.online && ms < oneHour;
}

interface ShuttleMarkerData {
  shuttle: Shuttle;
  marker: google.maps.Marker;
}

export const Map = ({ selectedShuttle, onShuttleSelected, pollInterval }: MapProps) => {

  const position = usePosition(); // geolocation hook

  const { data } = useQuery<ShuttleData>(GET_SHUTTLES, { variables: { lat: position.position?.lat, lng: position.position?.lng },
    pollInterval: pollInterval,
  });

  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [overlay, setOverlay] = useState<ITrackerOverlay>();
  const [markers, setMarkers] = useState<ShuttleMarkerData[]>([]);
  const [userPosition, setUserPosition] = useState<google.maps.LatLng|undefined>();
  const [userZoom, setUserZoom] = useState<number|undefined>();
  const hasInitiallyFit = useRef(false);

  // set the map once
  useEffect(() => {
    if (mapRef.current && !map) {
      console.log('create map [marker]');
      const shuttleMap = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: defaultZoom,//11,
        disableDefaultUI: true
      });

      shuttleMap.addListener('click', () => {
        onShuttleSelected(undefined);
      });

      shuttleMap.addListener('drag', () => {
        console.log('center changed');
        setUserPosition(shuttleMap.getCenter());
        setUserZoom(shuttleMap.getZoom());
      });

      setMap(shuttleMap);
    }
  }, [mapRef, map, onShuttleSelected]);

  // set the current position overlay once
  useEffect(() => {
    if (position.position && map && !overlay) {
      console.log('Tracker create overlay');

      class CurrentPositionOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div?: HTMLElement;
    
        constructor(position: google.maps.LatLng) {
          super();
          this.position = position;
        }

        setPosition(position: google.maps.LatLng) {
          console.log('current overlay position', position.lat(), position.lng());
          this.position = position;
          this.draw();
        }
    
        onAdd() {
          this.div = document.createElement('div');
          this.div.className = 'current-position-overlay';
          const panes = this.getPanes()!;
          panes.overlayLayer.appendChild(this.div);
        }
    
        draw() {
          const overlayProjection = this.getProjection();
          if (overlayProjection) {
            const point = overlayProjection.fromLatLngToDivPixel(
              this.position
            )!;
            if (this.div) {
              this.div.style.left = `${point.x - 8}px`;
              this.div.style.top = `${point.y - 8}px`;
            }
          }
        }
    
        onRemove() {
          if (this.div) {
            (this.div.parentNode as HTMLElement).removeChild(this.div);
            delete this.div;
          }
        }
      }

      let c = { lat: position.position.lat, lng: position.position.lng };

      const currentPositionOverlay = new CurrentPositionOverlay(new google.maps.LatLng(c.lat, c.lng));
      currentPositionOverlay.setMap(map);
      console.log('Setting map position overlay to ', c.lat, c.lng);
      map.setCenter(new google.maps.LatLng(c.lat, c.lng));
      setOverlay(currentPositionOverlay);

    }
  }, [position.position?.lat, position.position?.lng, overlay, map]);

  // keep the position overlay (blue dot) synced with the user
  useEffect(() => {
    if (overlay && position.position) {
      overlay.setPosition(new google.maps.LatLng(position.position.lat, position.position.lng));
    }
  }, [position.position?.lat, position.position?.lng, overlay]);

  // follow the user as they walk (only after the initial fit has happened)
  useEffect(() => {
    if (!hasInitiallyFit.current) return;
    if (!map || !position.position) return;
    if (userPosition || userZoom || selectedShuttle) return;
    map.setCenter(new google.maps.LatLng(position.position.lat, position.position.lng));
  }, [position.position?.lat, position.position?.lng, map, userPosition, userZoom, selectedShuttle]);

  // one-time fit on initial load: user + online shuttles
  useEffect(() => {
    if (hasInitiallyFit.current) return;
    if (!data || !map || !position.position) return;
    if (selectedShuttle || userPosition || userZoom) return;

    const bounds = new google.maps.LatLngBounds();
    for (const shuttle of data.shuttlesByLocation) {
      if (shuttle.lat && shuttle.lng && isOnline(shuttle)) {
        bounds.extend(new google.maps.LatLng(shuttle.lat, shuttle.lng));
      }
    }
    bounds.extend(new google.maps.LatLng(position.position.lat, position.position.lng));
    map.fitBounds(bounds);
    const zoom = map.getZoom();
    if (zoom && zoom > defaultZoom) {
      map.setZoom(defaultZoom);
    }
    hasInitiallyFit.current = true;
  }, [data, map, position.position?.lat, position.position?.lng, selectedShuttle, userPosition, userZoom]);

  // fit to the selected shuttle when one is picked
  useEffect(() => {
    if (!data || !map || !selectedShuttle || !isOnline(selectedShuttle)) return;
    const shuttle = data.shuttlesByLocation.find((s) => s.id === selectedShuttle.id);
    if (!shuttle) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(shuttle.lat, shuttle.lng));
    map.fitBounds(bounds);
    const zoom = map.getZoom();
    if (zoom && zoom > defaultZoom) {
      map.setZoom(defaultZoom);
    }
  }, [data, map, selectedShuttle]);

  // update markers based on filtered shuttles
  useEffect(() => {
    if (map) {
      const filteredShuttles = data?.shuttlesByLocation.filter((s) => {
        if (selectedShuttle) {
          return s.id === selectedShuttle.id && isOnline(s);
        }
        return isOnline(s);
      }) || [];
      for (const shuttle of filteredShuttles) {
        const position = new google.maps.LatLng(shuttle.lat, shuttle.lng);
        const shuttleMarker = markers.filter((m) => m.shuttle.id === shuttle.id)[0];
      
        if (!shuttleMarker) {
          // create the marker if it doesn't exist
          console.log('==== add marker', shuttle.name, position.lat(), position.lng());
          const parsedColor = shuttle.color.replace('#', '%23');
  
          const options = {
              position: position,
              map: map,
              icon: {
                  anchor: new google.maps.Point(20, 20),
                  url: `data:image/svg+xml;utf-8, \
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"> \
                  <circle cx="20" cy="20" r="20" fill="${parsedColor}"/> \
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M27.2 8.47998C29.6 8.47998 30.08 9.8427 30.08 12.32V22.3261V27.68C30.08 28.3964 29.818 28.857 29.3846 29.1469L29.6 29.12V30.56C29.6 31.0899 29.1699 31.52 28.64 31.52H26.72C26.1901 31.52 25.76 31.0899 25.76 30.56V29.6L25.7601 29.6H14.2399L14.24 29.6V30.56C14.24 31.0899 13.8099 31.52 13.28 31.52H11.36C10.8306 31.52 10.4 31.0899 10.4 30.56V29.12L10.6154 29.1469C10.182 28.857 9.91998 28.3964 9.91998 27.68V22.3261V11.84C9.91998 9.3027 10.88 8.47998 13.76 8.47998H27.2ZM30.08 14.24H31.04C31.3055 14.24 31.52 14.455 31.52 14.72V18.56C31.52 18.825 31.3055 19.04 31.04 19.04H30.08V14.24ZM8.95998 14.24H9.91998V19.04H8.95998C8.69502 19.04 8.47998 18.825 8.47998 18.56V14.72C8.47998 14.455 8.69502 14.24 8.95998 14.24ZM26.48 27.68C27.4078 27.68 28.16 26.9278 28.16 26C28.16 25.0722 27.4078 24.32 26.48 24.32C25.5521 24.32 24.8 25.0722 24.8 26C24.8 26.9278 25.5521 27.68 26.48 27.68ZM15.2 26C15.2 26.9278 14.4478 27.68 13.52 27.68C12.5921 27.68 11.84 26.9278 11.84 26C11.84 25.0722 12.5921 24.32 13.52 24.32C14.4478 24.32 15.2 25.0722 15.2 26ZM25.76 11.36C25.76 11.6249 25.5454 11.84 25.28 11.84H15.2C14.935 11.84 14.72 11.6249 14.72 11.36V10.4C14.72 10.135 14.935 9.91998 15.2 9.91998H25.28C25.5454 9.91998 25.76 10.135 25.76 10.4V11.36ZM13.28 21.92L26.72 21.8826C27.2225 21.8826 28.16 21.44 28.16 20.48V14.72C28.16 13.76 27.68 13.28 26.72 13.28H13.28C12.32 13.28 11.84 13.76 11.84 14.72V20.48C11.84 21.44 12.32 21.92 13.28 21.92Z" fill="white"/> \
                  <path d="M29.3846 29.1469L29.1066 28.7313C28.914 28.8602 28.8351 29.1045 28.9161 29.3216C28.9971 29.5388 29.2166 29.6718 29.4466 29.6431L29.3846 29.1469ZM29.6 29.12H30.1C30.1 28.9766 30.0384 28.8401 29.9309 28.7452C29.8234 28.6502 29.6803 28.6061 29.538 28.6239L29.6 29.12ZM25.76 29.6L25.698 29.1039C25.4478 29.1351 25.26 29.3478 25.26 29.6H25.76ZM25.7601 29.6L25.8221 30.0961C26.0842 30.0634 26.2756 29.8325 26.2592 29.5689C26.2428 29.3053 26.0242 29.1 25.7601 29.1V29.6ZM14.2399 29.6V29.1C13.9758 29.1 13.7573 29.3053 13.7409 29.5689C13.7245 29.8325 13.9158 30.0634 14.1779 30.0961L14.2399 29.6ZM14.24 29.6H14.74C14.74 29.3478 14.5523 29.1351 14.302 29.1039L14.24 29.6ZM10.4 29.12L10.462 28.6239C10.3197 28.6061 10.1766 28.6502 10.0691 28.7452C9.96161 28.8401 9.90002 28.9766 9.90002 29.12H10.4ZM10.6154 29.1469L10.5534 29.6431C10.7833 29.6718 11.0029 29.5388 11.0839 29.3216C11.1648 29.1045 11.086 28.8602 10.8934 28.7313L10.6154 29.1469ZM30.08 14.24V13.74C29.8039 13.74 29.58 13.9638 29.58 14.24H30.08ZM30.08 19.04H29.58C29.58 19.3161 29.8039 19.54 30.08 19.54V19.04ZM9.91998 14.24H10.42C10.42 13.9638 10.1961 13.74 9.91998 13.74V14.24ZM9.91998 19.04V19.54C10.1961 19.54 10.42 19.3161 10.42 19.04H9.91998ZM26.72 21.8826V21.3826L26.7186 21.3826L26.72 21.8826ZM13.28 21.92V22.42L13.2814 22.42L13.28 21.92ZM30.58 12.32C30.58 11.0806 30.4671 9.98489 29.96 9.19974C29.4146 8.35517 28.4974 7.97998 27.2 7.97998V8.97998C28.3025 8.97998 28.8254 9.28615 29.12 9.74226C29.4529 10.2578 29.58 11.0821 29.58 12.32H30.58ZM30.58 22.3261V12.32H29.58V22.3261H30.58ZM30.58 27.68V22.3261H29.58V27.68H30.58ZM29.6626 29.5625C30.2528 29.1677 30.58 28.5403 30.58 27.68H29.58C29.58 28.2525 29.3831 28.5463 29.1066 28.7313L29.6626 29.5625ZM29.538 28.6239L29.3226 28.6508L29.4466 29.6431L29.662 29.6161L29.538 28.6239ZM30.1 30.56V29.12H29.1V30.56H30.1ZM28.64 32.02C29.4461 32.02 30.1 31.3661 30.1 30.56H29.1C29.1 30.8138 28.8938 31.02 28.64 31.02V32.02ZM26.72 32.02H28.64V31.02H26.72V32.02ZM25.26 30.56C25.26 31.3661 25.9139 32.02 26.72 32.02V31.02C26.4662 31.02 26.26 30.8138 26.26 30.56H25.26ZM25.26 29.6V30.56H26.26V29.6H25.26ZM25.6981 29.1038L25.698 29.1039L25.822 30.0961L25.8221 30.0961L25.6981 29.1038ZM14.2399 30.1H25.7601V29.1H14.2399V30.1ZM14.302 29.1039L14.3019 29.1038L14.1779 30.0961L14.178 30.0961L14.302 29.1039ZM14.74 30.56V29.6H13.74V30.56H14.74ZM13.28 32.02C14.0861 32.02 14.74 31.3661 14.74 30.56H13.74C13.74 30.8138 13.5338 31.02 13.28 31.02V32.02ZM11.36 32.02H13.28V31.02H11.36V32.02ZM9.90002 30.56C9.90002 31.3662 10.5546 32.02 11.36 32.02V31.02C11.1066 31.02 10.9 30.8136 10.9 30.56H9.90002ZM9.90002 29.12V30.56H10.9V29.12H9.90002ZM10.6774 28.6508L10.462 28.6239L10.338 29.6161L10.5534 29.6431L10.6774 28.6508ZM9.41998 27.68C9.41998 28.5403 9.74719 29.1677 10.3374 29.5625L10.8934 28.7313C10.6168 28.5463 10.42 28.2525 10.42 27.68H9.41998ZM9.41998 22.3261V27.68H10.42V22.3261H9.41998ZM9.41998 11.84V22.3261H10.42V11.84H9.41998ZM13.76 7.97998C12.3044 7.97998 11.1789 8.18027 10.4315 8.83154C9.663 9.50121 9.41998 10.531 9.41998 11.84H10.42C10.42 10.6116 10.657 9.96147 11.0885 9.58546C11.5411 9.19105 12.3355 8.97998 13.76 8.97998V7.97998ZM27.2 7.97998H13.76V8.97998H27.2V7.97998ZM31.04 13.74H30.08V14.74H31.04V13.74ZM32.02 14.72C32.02 14.1792 31.5819 13.74 31.04 13.74V14.74C31.029 14.74 31.02 14.7308 31.02 14.72H32.02ZM32.02 18.56V14.72H31.02V18.56H32.02ZM31.04 19.54C31.5819 19.54 32.02 19.1008 32.02 18.56H31.02C31.02 18.5491 31.029 18.54 31.04 18.54V19.54ZM30.08 19.54H31.04V18.54H30.08V19.54ZM29.58 14.24V19.04H30.58V14.24H29.58ZM9.91998 13.74H8.95998V14.74H9.91998V13.74ZM10.42 19.04V14.24H9.41998V19.04H10.42ZM8.95998 19.54H9.91998V18.54H8.95998V19.54ZM7.97998 18.56C7.97998 19.1011 8.41888 19.54 8.95998 19.54V18.54C8.96193 18.54 8.96499 18.5405 8.96807 18.5418C8.97081 18.5429 8.97277 18.5444 8.97417 18.5458C8.97557 18.5472 8.97702 18.5492 8.97818 18.5519C8.97949 18.555 8.97998 18.558 8.97998 18.56H7.97998ZM7.97998 14.72V18.56H8.97998V14.72H7.97998ZM8.95998 13.74C8.41888 13.74 7.97998 14.1789 7.97998 14.72H8.97998C8.97998 14.7219 8.97949 14.725 8.97818 14.7281C8.97702 14.7308 8.97557 14.7328 8.97417 14.7342C8.97277 14.7356 8.97081 14.737 8.96807 14.7382C8.96499 14.7395 8.96193 14.74 8.95998 14.74V13.74ZM27.66 26C27.66 26.6517 27.1317 27.18 26.48 27.18V28.18C27.684 28.18 28.66 27.204 28.66 26H27.66ZM26.48 24.82C27.1317 24.82 27.66 25.3483 27.66 26H28.66C28.66 24.796 27.684 23.82 26.48 23.82V24.82ZM25.3 26C25.3 25.3483 25.8283 24.82 26.48 24.82V23.82C25.276 23.82 24.3 24.796 24.3 26H25.3ZM26.48 27.18C25.8283 27.18 25.3 26.6517 25.3 26H24.3C24.3 27.204 25.276 28.18 26.48 28.18V27.18ZM13.52 28.18C14.7239 28.18 15.7 27.204 15.7 26H14.7C14.7 26.6517 14.1717 27.18 13.52 27.18V28.18ZM11.34 26C11.34 27.204 12.316 28.18 13.52 28.18V27.18C12.8683 27.18 12.34 26.6517 12.34 26H11.34ZM13.52 23.82C12.316 23.82 11.34 24.796 11.34 26H12.34C12.34 25.3483 12.8683 24.82 13.52 24.82V23.82ZM15.7 26C15.7 24.796 14.7239 23.82 13.52 23.82V24.82C14.1717 24.82 14.7 25.3483 14.7 26H15.7ZM25.28 12.34C25.8219 12.34 26.26 11.9008 26.26 11.36H25.26C25.26 11.3491 25.2689 11.34 25.28 11.34V12.34ZM15.2 12.34H25.28V11.34H15.2V12.34ZM14.22 11.36C14.22 11.9011 14.6589 12.34 15.2 12.34V11.34C15.2019 11.34 15.205 11.3405 15.2081 11.3418C15.2108 11.3429 15.2128 11.3444 15.2142 11.3458C15.2156 11.3472 15.217 11.3492 15.2182 11.3519C15.2195 11.355 15.22 11.358 15.22 11.36H14.22ZM14.22 10.4V11.36H15.22V10.4H14.22ZM15.2 9.41998C14.6589 9.41998 14.22 9.85888 14.22 10.4H15.22C15.22 10.4019 15.2195 10.405 15.2182 10.4081C15.217 10.4108 15.2156 10.4128 15.2142 10.4142C15.2128 10.4156 15.2108 10.417 15.2081 10.4182C15.205 10.4195 15.2019 10.42 15.2 10.42V9.41998ZM25.28 9.41998H15.2V10.42H25.28V9.41998ZM26.26 10.4C26.26 9.8592 25.8219 9.41998 25.28 9.41998V10.42C25.2689 10.42 25.26 10.4108 25.26 10.4H26.26ZM26.26 11.36V10.4H25.26V11.36H26.26ZM26.7186 21.3826L13.2786 21.42L13.2814 22.42L26.7214 22.3826L26.7186 21.3826ZM27.66 20.48C27.66 20.776 27.5225 20.9906 27.3219 21.1463C27.1054 21.3143 26.8518 21.3826 26.72 21.3826V22.3826C27.0907 22.3826 27.557 22.2296 27.935 21.9363C28.3288 21.6307 28.66 21.1441 28.66 20.48H27.66ZM27.66 14.72V20.48H28.66V14.72H27.66ZM26.72 13.78C27.1229 13.78 27.3333 13.8804 27.4464 13.9936C27.5596 14.1067 27.66 14.3171 27.66 14.72H28.66C28.66 14.163 28.5204 13.6533 28.1535 13.2865C27.7867 12.9196 27.277 12.78 26.72 12.78V13.78ZM13.28 13.78H26.72V12.78H13.28V13.78ZM12.34 14.72C12.34 14.3171 12.4404 14.1067 12.5535 13.9936C12.6667 13.8804 12.877 13.78 13.28 13.78V12.78C12.7229 12.78 12.2133 12.9196 11.8464 13.2865C11.4796 13.6533 11.34 14.163 11.34 14.72H12.34ZM12.34 20.48V14.72H11.34V20.48H12.34ZM13.28 21.42C12.877 21.42 12.6667 21.3196 12.5535 21.2065C12.4404 21.0933 12.34 20.883 12.34 20.48H11.34C11.34 21.0371 11.4796 21.5467 11.8464 21.9136C12.2133 22.2804 12.7229 22.42 13.28 22.42V21.42Z" fill="white"/> \
                  </svg>`
              }
          };
  
          const marker = new google.maps.Marker(options);
          marker.addListener('click', () => {
              onShuttleSelected(shuttle);
          });
          setMarkers(arr => [...arr, { shuttle, marker }]);
        } else {
          // just update the position if the marker already exists
          shuttleMarker.marker.setPosition(position);
        }

      }

      // filter out hidden shuttles
      for (const marker of markers) {
        const shuttle = filteredShuttles.filter((s) => s.id === marker.shuttle.id)[0];
        if (shuttle) {
          marker.marker.setMap(map);
        } else {
          marker.marker.setMap(null);
        }
      }
    }
  }, [data, markers, selectedShuttle, onShuttleSelected, map]);

  const centerPosition = () => {
    console.log('center clicked');
    // Try HTML5 geolocation.
    if (map) {
      console.log('map');
      if (navigator.geolocation) {
        console.log('nav geo');
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            console.log('map center', pos);
            var bounds = new google.maps.LatLngBounds();
            if (data) {
              for (const shuttle of data.shuttlesByLocation) {
                if (shuttle.lat && shuttle.lng && isOnline(shuttle)) {
                  const pos = new google.maps.LatLng(shuttle.lat, shuttle.lng);
                  bounds.extend(pos);
                }
              }
            }
            bounds.extend(pos);
            map.fitBounds(bounds);
            const zoom = map.getZoom();
            if (zoom && zoom > defaultZoom) {
              map.setZoom(defaultZoom);
            }
            // re-enable follow mode
            setUserPosition(undefined);
            setUserZoom(undefined);
          },
          (e) => {
            console.error('Tracker error getting location', e);
          },
          { enableHighAccuracy: true }
        );
      } else {
        // Browser doesn't support Geolocation
        console.error('Tracker browser does not support geolocation');
      }
    }
  }

  return (
    <div className="h-full pb-20 text-neutral relative">
      <div id="map" ref={mapRef} className="h-full"/>
      {!selectedShuttle &&
        <button onClick={centerPosition} className="focus:outline-none absolute bottom-32 right-4 bg-primary rounded-full text-white text-center text-s p-2">
          <SVG src={markerIcon} className="m-auto w-5 h-5" />
        </button>
      }
    </div>
  );
};

export default Map;
